import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import ArticleList from './components/ArticleList';
import { ArticleForm } from './components/ArticleEditor';
import PatchNoteList from './components/PatchNoteList';
import { PatchNoteForm } from './components/PatchNoteEditor';
import type { Article, ArticleDraft } from '../../types/article';
import type { PatchNote, PatchNoteDraft } from '../../types/patchNote';
import { slugify } from '../../utils/slugify';
import SEOHead from '../../components/feature/SEOHead';

type View = 'list' | 'new' | 'edit';
type Section = 'articles' | 'patch-notes';

export default function AdminPage() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [section, setSection] = useState<Section>('articles');

  // Articles state
  const [articleView, setArticleView] = useState<View>('list');
  const [editingArticle, setEditingArticle] = useState<Article | null>(null);
  const [articleSubmitting, setArticleSubmitting] = useState(false);

  // Patch notes state
  const [patchView, setPatchView] = useState<View>('list');
  const [editingPatch, setEditingPatch] = useState<PatchNote | null>(null);
  const [patchSubmitting, setPatchSubmitting] = useState(false);

  const [toast, setToast] = useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) {
        navigate('/admin/login');
      } else {
        setChecking(false);
      }
    });
  }, [navigate]);

  // ── Article handlers ──────────────────────────────────
  const handleCreateArticle = async (data: ArticleDraft, publish: boolean) => {
    setArticleSubmitting(true);
    const slug = slugify(data.title);
    const { error } = await supabase.from('articles').insert([{ ...data, slug, published: publish }]);
    setArticleSubmitting(false);
    if (error) { showToast('Error saving article. Please try again.'); return; }
    showToast(publish ? 'Article published!' : 'Draft saved!');
    setArticleView('list');
  };

  const handleUpdateArticle = async (data: ArticleDraft, publish: boolean) => {
    if (!editingArticle) return;
    setArticleSubmitting(true);
    const slug = data.slug?.trim() ? data.slug : slugify(data.title);
    const { error } = await supabase
      .from('articles')
      .update({ ...data, slug, published: publish, updated_at: new Date().toISOString() })
      .eq('id', editingArticle.id);
    setArticleSubmitting(false);
    if (error) { showToast('Error updating article. Please try again.'); return; }
    showToast(publish ? 'Article updated and live!' : 'Draft updated.');
    setArticleView('list');
    setEditingArticle(null);
  };

  // ── Patch note handlers ───────────────────────────────
  const handleCreatePatch = async (data: PatchNoteDraft, publish: boolean) => {
    setPatchSubmitting(true);
    const { error } = await supabase.from('patch_notes').insert([{ ...data, published: publish }]);
    setPatchSubmitting(false);
    if (error) { showToast('Error saving patch note. Please try again.'); return; }
    showToast(publish ? 'Patch note published!' : 'Draft saved!');
    setPatchView('list');
  };

  const handleUpdatePatch = async (data: PatchNoteDraft, publish: boolean) => {
    if (!editingPatch) return;
    setPatchSubmitting(true);
    const { error } = await supabase
      .from('patch_notes')
      .update({ ...data, published: publish, updated_at: new Date().toISOString() })
      .eq('id', editingPatch.id);
    setPatchSubmitting(false);
    if (error) { showToast('Error updating patch note. Please try again.'); return; }
    showToast(publish ? 'Patch note live!' : 'Draft updated.');
    setPatchView('list');
    setEditingPatch(null);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login');
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-[#00ff41] text-3xl" />
      </div>
    );
  }

  // Determine if we're in editor mode
  const inArticleEditor = section === 'articles' && (articleView === 'new' || articleView === 'edit');
  const inPatchEditor = section === 'patch-notes' && (patchView === 'new' || patchView === 'edit');
  const inEditor = inArticleEditor || inPatchEditor;

  const editorTitle = inArticleEditor
    ? (articleView === 'new' ? 'New Article' : 'Edit Article')
    : (patchView === 'new' ? 'New Patch Note' : 'Edit Patch Note');

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEOHead
        title="Admin — Command Center | CallOfDutyNext.com"
        description="Admin command center."
        canonical="/admin"
        noIndex
      />
      {/* Toast */}
      {toast && (
        <div className="fixed top-6 right-6 z-50 bg-[#00ff41] text-black text-sm font-['Barlow_Condensed'] font-bold px-5 py-3 rounded-md">
          {toast}
        </div>
      )}

      {/* Top Bar */}
      <header className="border-b border-[#1f1f1f] bg-[#0d0d0d] px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <a href="/" className="font-['Barlow_Condensed'] font-black text-xl tracking-tight text-white whitespace-nowrap">
            CALLOFDUTY<span className="text-[#00ff41]">NEXT</span>
          </a>
          <span className="text-[#2a2a2a]">|</span>
          <span className="font-['Barlow_Condensed'] font-bold text-gray-500 tracking-widest text-sm uppercase">
            {inEditor ? editorTitle : 'Command Center'}
          </span>
        </div>
        <div className="flex items-center gap-4">
          {inEditor ? (
            <button
              onClick={() => {
                if (inArticleEditor) { setArticleView('list'); setEditingArticle(null); }
                else { setPatchView('list'); setEditingPatch(null); }
              }}
              className="text-gray-500 hover:text-white text-sm transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" /> Back to Dashboard
            </button>
          ) : (
            <>
              <a href="/news" target="_blank" rel="nofollow noopener noreferrer" className="text-gray-500 hover:text-white text-sm transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap">
                <i className="ri-external-link-line" /> View Live Site
              </a>
              <button
                onClick={handleLogout}
                className="border border-[#2a2a2a] text-gray-500 hover:text-white hover:border-[#444] text-sm px-4 py-2 rounded-md transition-all cursor-pointer whitespace-nowrap font-['Barlow_Condensed'] font-bold tracking-wider"
              >
                LOGOUT
              </button>
            </>
          )}
        </div>
      </header>

      {/* Section tabs — only show on list view */}
      {!inEditor && (
        <div className="border-b border-[#1f1f1f] bg-[#0d0d0d] px-8">
          <div className="max-w-6xl mx-auto flex items-center gap-0">
            {([
              { id: 'articles', label: 'Articles', icon: 'ri-article-line' },
              { id: 'patch-notes', label: 'Patch Notes', icon: 'ri-file-list-3-line' },
            ] as { id: Section; label: string; icon: string }[]).map((tab) => (
              <button
                key={tab.id}
                onClick={() => setSection(tab.id)}
                className={`flex items-center gap-2 px-5 py-3.5 font-['Barlow_Condensed'] font-bold text-sm tracking-widest border-b-2 transition-all cursor-pointer whitespace-nowrap ${
                  section === tab.id
                    ? 'border-[#00ff41] text-white'
                    : 'border-transparent text-gray-500 hover:text-gray-300'
                }`}
              >
                <i className={tab.icon} />
                {tab.label.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-8 py-10">
        {/* Articles section */}
        {section === 'articles' && articleView === 'list' && (
          <ArticleList
            onNew={() => setArticleView('new')}
            onEdit={(article) => { setEditingArticle(article); setArticleView('edit'); }}
            hideHeader
          />
        )}

        {inArticleEditor && (
          <>
            <h2 className="font-['Barlow_Condensed'] font-black text-white text-4xl mb-8">
              {articleView === 'new' ? 'Write New Article' : 'Edit Article'}
            </h2>
            <div className="bg-[#111] border border-[#1f1f1f] rounded-lg p-8">
              <ArticleForm
                initial={editingArticle ?? undefined}
                onSubmit={articleView === 'new' ? handleCreateArticle : handleUpdateArticle}
                onCancel={() => { setArticleView('list'); setEditingArticle(null); }}
                submitting={articleSubmitting}
              />
            </div>
          </>
        )}

        {/* Patch notes section */}
        {section === 'patch-notes' && patchView === 'list' && (
          <PatchNoteList
            onNew={() => setPatchView('new')}
            onEdit={(patch) => { setEditingPatch(patch); setPatchView('edit'); }}
          />
        )}

        {inPatchEditor && (
          <>
            <h2 className="font-['Barlow_Condensed'] font-black text-white text-4xl mb-8">
              {patchView === 'new' ? 'New Patch Note' : 'Edit Patch Note'}
            </h2>
            <div className="bg-[#111] border border-[#1f1f1f] rounded-lg p-8">
              <PatchNoteForm
                initial={editingPatch ?? undefined}
                onSubmit={patchView === 'new' ? handleCreatePatch : handleUpdatePatch}
                onCancel={() => { setPatchView('list'); setEditingPatch(null); }}
                submitting={patchSubmitting}
              />
            </div>
          </>
        )}
      </main>
    </div>
  );
}
